import { v4 as uuid } from 'uuid';
import { BarChart2, CheckSquare, Sliders, Trophy } from 'lucide-react';
import toast from 'react-hot-toast';
import { useEditorStore } from '../../store/editorStore';

const INTERACTIVE_TYPES = [
  {
    id: 'binary-poll',
    icon: <BarChart2 size={20} />,
    label: 'Binary Poll',
    description: 'Quick 2-option yes/no poll',
    // Categorical: these 5 gradients identify interactive element types and must
    // stay distinct from each other, so they are NOT brand-linked.
    gradient: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
    create: () => ({
      prompt: 'What do you think?',
      options: [
        { id: uuid(), text: 'Yes! 👍' },
        { id: uuid(), text: 'No ❌' },
      ],
    }),
  },
  {
    id: 'text-poll',
    icon: <BarChart2 size={20} />,
    label: 'Text Poll',
    description: 'Multi-option text poll',
    gradient: 'linear-gradient(135deg, #7c3aed, #db2777)',
    create: () => ({
      prompt: 'Choose an option',
      options: [
        { id: uuid(), text: 'Option A' },
        { id: uuid(), text: 'Option B' },
        { id: uuid(), text: 'Option C' },
        { id: uuid(), text: 'Option D' },
      ],
    }),
  },
  {
    id: 'text-quiz',
    icon: <CheckSquare size={20} />,
    label: 'Quiz',
    description: 'Multiple choice quiz question',
    gradient: 'linear-gradient(135deg, #059669, #0891b2)',
    create: () => ({
      prompt: 'What is the correct answer?',
      options: [
        { id: uuid(), text: 'Option A', isCorrect: true },
        { id: uuid(), text: 'Option B', isCorrect: false },
        { id: uuid(), text: 'Option C', isCorrect: false },
        { id: uuid(), text: 'Option D', isCorrect: false },
      ],
    }),
  },
  {
    id: 'slider',
    icon: <Sliders size={20} />,
    label: 'Slider',
    description: 'Interactive emoji slider',
    gradient: 'linear-gradient(135deg, #ea580c, #d97706)',
    create: () => ({
      prompt: 'How much do you agree?',
      sliderMin: 0,
      sliderMax: 100,
      sliderStep: 1,
      sliderInitialValue: 50,
    }),
  },
  {
    id: 'results',
    icon: <Trophy size={20} />,
    label: 'Results Screen',
    description: 'Show quiz/poll results',
    gradient: 'linear-gradient(135deg, #0284c7, #0ea5e9)',
    create: () => ({
      prompt: 'Here are your results!',
      resultsCategories: [
        { id: uuid(), title: 'Expert!', description: 'You scored high.', threshold: 80 },
        { id: uuid(), title: 'Getting there', description: 'Keep learning.', threshold: 50 },
        { id: uuid(), title: 'Beginner', description: 'Start from the basics.', threshold: 0 },
      ],
    }),
  },
];

export default function InteractivePanel() {
  const activePageId = useEditorStore((s) => s.activePageId);
  const addLayer = useEditorStore((s) => s.addLayer);

  function handleAdd(item) {
    if (!activePageId) {
      toast.error('No page selected — click a page in the bottom strip first');
      return;
    }

    const interactionId = uuid();
    const extra = item.create();

    addLayer(activePageId, {
      type: 'interactive',
      interactiveType: item.id,
      interactionId,
      theme: 'dark',
      ...extra,
    }, {
      x: 5, y: 30, width: 90, height: 35,
    });

    toast.success(`${item.label} added!`);
  }

  return (
    <div className="p-2">
      <h3 className="panel-label mb-1">Interactive Elements</h3>
      <p className="text-xs mb-3" style={{ color: 'var(--t-muted)' }}>
        AMP-native polls, quizzes, and sliders that collect user responses.
      </p>

      <div className="space-y-2">
        {INTERACTIVE_TYPES.map((item) => (
          <button
            key={item.id}
            onClick={() => handleAdd(item)}
            className="w-full text-left rounded-lg p-3 transition-all"
            style={{
              background: item.gradient,
              border: '1px solid rgba(255,255,255,0.15)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.12)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex-shrink-0" style={{ color: '#ffffff' }}>
                {item.icon}
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: '#ffffff' }}>{item.label}</p>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.65)' }}>{item.description}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-4 p-2 rounded-lg border" style={{ background: 'var(--t-panel)', borderColor: 'var(--t-border)' }}>
        <p className="text-xs" style={{ color: 'var(--t-muted)' }}>
          <span className="text-indigo-400 font-medium">Note:</span> Interactive elements require a backend
          endpoint to collect and display responses. Configure the endpoint in the element's properties.
        </p>
      </div>
    </div>
  );
}
